import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const SECTIONS = [
  { id: 'overview',           icon: '🔍' },
  { id: 'quickstart',         icon: '🚀' },
  { id: 'architecture',       icon: '🏗️' },
  { id: 'cli',                icon: '💻' },
  { id: 'queryExecution',     icon: '🔎' },
  { id: 'lsp',                icon: '🖥️' },
  { id: 'cicd',               icon: '🔄' },
  { id: 'sarif',              icon: '📤' },
  { id: 'sastIntegration',    icon: '🔬' },
  { id: 'baseline',           icon: '📌' },
  { id: 'suppression',        icon: '🔇' },
  { id: 'testingQuality',     icon: '🧪' },
  { id: 'benchmark',          icon: '📊' },
  { id: 'registry',           icon: '📦' },
  { id: 'ruleDocumentation',  icon: '📄' },
  { id: 'ruleStudio',         icon: '🎨' },
  { id: 'academy',            icon: '🎓' },
  { id: 'dsl',                icon: '📐' },
  { id: 'findTypes',          icon: '🔦' },
  { id: 'concepts',           icon: '🎯' },
  { id: 'expressions',        icon: '🔗' },
  { id: 'conditions',         icon: '⚙️' },
  { id: 'engines',            icon: '⚡' },
  { id: 'captures',           icon: '📍' },
  { id: 'aiAssistant',        icon: '🤖' },
  { id: 'builtinRules',       icon: '📋' },
  { id: 'api',                icon: '🔌' },
];

function SectionHeading({ icon, title }) {
  return (
    <h2 style={{ fontSize: '1.375rem', fontWeight: 700, marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <span>{icon}</span> {title}
    </h2>
  );
}

function CodeBlock({ code }) {
  return (
    <pre style={{ background: '#1e1e1e', color: '#d4d4d4', padding: '1rem', borderRadius: '0.5rem', fontSize: '0.82rem', fontFamily: "'JetBrains Mono', 'Menlo', monospace", overflowX: 'auto', whiteSpace: 'pre', lineHeight: 1.5 }}>
      <code>{code}</code>
    </pre>
  );
}

function SeverityBadge({ severity }) {
  const colors = { critical: 'danger', high: 'warning', medium: 'info', low: 'secondary', info: 'light' };
  return <span className={`badge bg-${colors[severity] || 'secondary'} me-1`}>{severity}</span>;
}

function BulletList({ items }) {
  if (!items || !Array.isArray(items)) return null;
  return (
    <ul style={{ lineHeight: 1.8, paddingLeft: '1.25rem', marginBottom: 0 }}>
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
}

function ConceptCategory({ category }) {
  return (
    <div className="card shadow-sm mb-3">
      <div className="card-body">
        <div className="fw-semibold mb-1">{category.name}</div>
        <div className="small text-secondary mb-2">{category.description}</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
          {category.items.map((item) => (
            <span key={item.id} title={item.description} style={{
              fontSize: '0.75rem', fontWeight: 600, padding: '0.2rem 0.6rem', borderRadius: '100px',
              background: 'var(--bs-tertiary-bg)', color: 'var(--bs-secondary-color)',
              border: '1px solid var(--bs-border-color)', fontFamily: "'JetBrains Mono', 'Menlo', monospace",
              cursor: 'default',
            }}>
              {item.id}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function VigilDocs() {
  const { t } = useTranslation();

  const renderSection = (section) => {
    const base = `vigilDocs.sections.${section.id}`;
    const body = t(`${base}.body`);

    switch (section.id) {
      case 'overview':
        return (
          <>
            <p style={{ lineHeight: 1.75 }}>{body}</p>
            <BulletList items={t(`${base}.bullets`, { returnObjects: true })} />
          </>
        );

      case 'quickstart': {
        const steps = t(`${base}.steps`, { returnObjects: true });
        return (
          <>
            <p style={{ lineHeight: 1.75 }}>{body}</p>
            {Array.isArray(steps) && steps.map((step, i) => (
              <div key={i} className="mb-3">
                <div className="fw-semibold mb-1">{i + 1}. {step.title}</div>
                <CodeBlock code={step.code} />
              </div>
            ))}
          </>
        );
      }

      case 'architecture': {
        const layers = t(`${base}.layers`, { returnObjects: true });
        return (
          <>
            <p style={{ lineHeight: 1.75 }}>{body}</p>
            {Array.isArray(layers) && layers.map((layer, i) => (
              <div key={i} className="card shadow-sm mb-2">
                <div className="card-body py-2 px-3">
                  <div className="fw-semibold">{layer.name}</div>
                  <div className="small text-secondary">{layer.description}</div>
                </div>
              </div>
            ))}
          </>
        );
      }

      case 'cli': {
        const commands = t(`${base}.commands`, { returnObjects: true });
        return (
          <>
            <p style={{ lineHeight: 1.75 }}>{body}</p>
            {Array.isArray(commands) && commands.map((cmd, i) => (
              <div key={i} className="mb-3">
                <div className="fw-semibold font-monospace">{cmd.name}</div>
                <div className="small text-secondary mb-1">{cmd.description}</div>
                <CodeBlock code={cmd.usage} />
              </div>
            ))}
          </>
        );
      }

      case 'queryExecution': {
        const modes = t(`${base}.modes`, { returnObjects: true });
        const flags = t(`${base}.flags`, { returnObjects: true });
        return (
          <>
            <p style={{ lineHeight: 1.75 }}>{body}</p>
            {Array.isArray(modes) && (
              <div className="mb-3">
                <h4 style={{ fontSize: '1rem', fontWeight: 600 }}>Two Modes</h4>
                <div className="table-responsive">
                  <table className="table table-sm table-bordered" style={{ fontSize: '0.85rem' }}>
                    <thead><tr><th>Mode</th><th>When</th><th>What happens</th></tr></thead>
                    <tbody>
                      {modes.map((m, i) => (
                        <tr key={i}>
                          <td className="fw-bold">{m.name}</td>
                          <td className="font-monospace" style={{ fontSize: '0.8rem' }}>{m.when}</td>
                          <td>{m.what}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {Array.isArray(flags) && (
              <div className="mb-3">
                <h4 style={{ fontSize: '1rem', fontWeight: 600 }}>Flags</h4>
                <div className="table-responsive">
                  <table className="table table-sm table-bordered" style={{ fontSize: '0.85rem' }}>
                    <thead><tr><th>Flag</th><th>Default</th><th>Description</th></tr></thead>
                    <tbody>
                      {flags.map((f, i) => (
                        <tr key={i}>
                          <td className="font-monospace">{f.flag}</td>
                          <td className="font-monospace text-secondary">{f.default}</td>
                          <td>{f.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {t(`${base}.example`, { defaultValue: '' }) && (
              <CodeBlock code={t(`${base}.example`)} />
            )}
          </>
        );
      }

      case 'lsp': {
        const methods = t(`${base}.methods`, { returnObjects: true });
        const flags   = t(`${base}.flags`, { returnObjects: true });
        return (
          <>
            <p style={{ lineHeight: 1.75 }}>{body}</p>

            <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1rem' }}>Start the Server</h4>
            <CodeBlock code={t(`${base}.startCode`, { defaultValue: 'vigil lsp --rules ./rules' })} />

            {Array.isArray(flags) && flags.length > 0 && (
              <div className="mb-3 mt-3">
                <h4 style={{ fontSize: '1rem', fontWeight: 600 }}>Flags</h4>
                <div className="table-responsive">
                  <table className="table table-sm table-bordered" style={{ fontSize: '0.85rem' }}>
                    <thead><tr><th>Flag</th><th>Default</th><th>Description</th></tr></thead>
                    <tbody>
                      {flags.map((f, i) => (
                        <tr key={i}>
                          <td className="font-monospace">{f.flag}</td>
                          <td className="text-secondary">{f.default}</td>
                          <td>{f.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {t(`${base}.vscodeTitle`, { defaultValue: '' }) && (
              <>
                <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1.25rem' }}>{t(`${base}.vscodeTitle`)}</h4>
                <CodeBlock code={t(`${base}.vscodeCode`, { defaultValue: '' })} />
              </>
            )}

            {t(`${base}.nvimTitle`, { defaultValue: '' }) && (
              <>
                <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1.25rem' }}>{t(`${base}.nvimTitle`)}</h4>
                <CodeBlock code={t(`${base}.nvimCode`, { defaultValue: '' })} />
              </>
            )}

            {Array.isArray(methods) && methods.length > 0 && (
              <div className="mt-3">
                <h4 style={{ fontSize: '1rem', fontWeight: 600 }}>Supported LSP Methods</h4>
                <div className="table-responsive">
                  <table className="table table-sm table-bordered" style={{ fontSize: '0.85rem' }}>
                    <thead><tr><th>Method</th><th>Direction</th><th>Purpose</th></tr></thead>
                    <tbody>
                      {methods.map((m, i) => (
                        <tr key={i}>
                          <td className="font-monospace" style={{ fontSize: '0.8rem' }}>{m.method}</td>
                          <td className="text-secondary" style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{m.direction}</td>
                          <td>{m.purpose}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        );
      }

      case 'dsl': {
        const grammar = t(`${base}.grammar`);
        return (
          <>
            <p style={{ lineHeight: 1.75 }}>{body}</p>
            <CodeBlock code={grammar} />
          </>
        );
      }

      case 'findTypes': {
        const types = t(`${base}.types`, { returnObjects: true });
        return (
          <>
            <p style={{ lineHeight: 1.75 }}>{body}</p>
            {Array.isArray(types) && types.map((ft, i) => (
              <div key={i} className="mb-3">
                <div className="fw-semibold font-monospace">{ft.name}</div>
                <div className="small text-secondary mb-1">{ft.description}</div>
                <CodeBlock code={ft.example} />
              </div>
            ))}
          </>
        );
      }

      case 'concepts': {
        const categories = t(`${base}.categories`, { returnObjects: true });
        return (
          <>
            <p style={{ lineHeight: 1.75 }}>{body}</p>
            <div className="row g-3 mt-1">
              {Array.isArray(categories) && categories.map((cat, i) => (
                <div key={i} className="col-12 col-xl-6">
                  <ConceptCategory category={cat} />
                </div>
              ))}
            </div>
          </>
        );
      }

      case 'expressions': {
        const items = t(`${base}.items`, { returnObjects: true });
        return (
          <>
            <p style={{ lineHeight: 1.75 }}>{body}</p>
            {Array.isArray(items) && items.map((item, i) => (
              <div key={i} className="mb-3">
                <div className="fw-semibold font-monospace">{item.name}</div>
                <div className="small text-secondary mb-1">{item.description}</div>
                <CodeBlock code={item.example} />
              </div>
            ))}
          </>
        );
      }

      case 'conditions': {
        const items = t(`${base}.items`, { returnObjects: true });
        return (
          <>
            <p style={{ lineHeight: 1.75 }}>{body}</p>
            <div className="table-responsive">
              <table className="table table-sm table-bordered" style={{ fontSize: '0.85rem' }}>
                <thead>
                  <tr><th>Syntax</th><th>Description</th></tr>
                </thead>
                <tbody>
                  {Array.isArray(items) && items.map((item, i) => (
                    <tr key={i}>
                      <td className="font-monospace" style={{ whiteSpace: 'nowrap' }}>{item.syntax}</td>
                      <td>{item.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        );
      }

      case 'engines': {
        const engines = t(`${base}.engines`, { returnObjects: true });
        const matrix  = t(`${base}.capabilityMatrix`, { returnObjects: true });
        return (
          <>
            <p style={{ lineHeight: 1.75 }}>{body}</p>
            {Array.isArray(matrix) && matrix.length > 0 ? (
              <>
                <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1rem' }}>Capability Matrix</h4>
                <div className="table-responsive">
                  <table className="table table-sm table-bordered" style={{ fontSize: '0.82rem' }}>
                    <thead>
                      <tr><th>Engine</th><th>Available</th><th>Flow</th><th>Call</th><th>Pattern</th><th>AST</th><th>Notes</th></tr>
                    </thead>
                    <tbody>
                      {matrix.map((eng, i) => (
                        <tr key={i}>
                          <td className="font-monospace fw-bold">{eng.engine}</td>
                          <td>{eng.available}</td>
                          <td>{eng.flow}</td>
                          <td>{eng.call}</td>
                          <td>{eng.pattern}</td>
                          <td>{eng.ast}</td>
                          <td className="text-secondary" style={{ fontSize: '0.78rem' }}>{eng.notes}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="small text-secondary mt-1">★★★ optimal &nbsp;★★ good &nbsp;★ fallback &nbsp;— n/a</p>
              </>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm table-bordered" style={{ fontSize: '0.85rem' }}>
                  <thead>
                    <tr><th>Engine</th><th>Strength</th><th>Languages</th><th>Use Cases</th></tr>
                  </thead>
                  <tbody>
                    {Array.isArray(engines) && engines.map((eng, i) => (
                      <tr key={i}>
                        <td className="font-monospace fw-bold">{eng.name}</td>
                        <td>{eng.strength}</td>
                        <td>{eng.languages}</td>
                        <td>{eng.useCases}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {t(`${base}.autoSelection`, { defaultValue: '' }) && (
              <div className="mt-3">
                <h4 style={{ fontSize: '1rem', fontWeight: 600 }}>Auto Engine Selection</h4>
                <p style={{ lineHeight: 1.75 }}>{t(`${base}.autoSelection`)}</p>
                <CodeBlock code={t(`${base}.cliExample`, { defaultValue: 'vigil scan --engine auto --rules ./rules ./src' })} />
              </div>
            )}
            {(() => {
              const routing = t(`${base}.findTypeRouting`, { returnObjects: true });
              if (!Array.isArray(routing) || routing.length === 0) return null;
              return (
                <div className="mt-3">
                  <h4 style={{ fontSize: '1rem', fontWeight: 600 }}>{t(`${base}.findTypeRoutingTitle`, { defaultValue: 'Find-Type → Engine Routing' })}</h4>
                  <p className="small text-secondary" style={{ lineHeight: 1.6 }}>{t(`${base}.findTypeRoutingNote`, { defaultValue: '' })}</p>
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered" style={{ fontSize: '0.82rem' }}>
                      <thead className="table-light">
                        <tr><th>Find type</th><th>★★★ Optimal</th><th>★★ Good</th><th>★ Fallback</th></tr>
                      </thead>
                      <tbody>
                        {routing.map((row, i) => (
                          <tr key={i}>
                            <td className="font-monospace">{row.findType}</td>
                            <td className="text-success fw-semibold">{row.optimal}</td>
                            <td>{row.good}</td>
                            <td className="text-secondary">{row.fallback}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="small text-secondary mt-1">Opengrep is always the final fallback. Omit <code>engine:</code> in a rule to guarantee it always runs.</p>
                </div>
              );
            })()}
          </>
        );
      }

      case 'captures': {
        const stateValues = t(`${base}.stateValues`, { returnObjects: true });
        return (
          <>
            <p style={{ lineHeight: 1.75 }}>{body}</p>

            {t(`${base}.example`, { defaultValue: '' }) && (
              <>
                <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1rem' }}>Captures & Context</h4>
                <CodeBlock code={t(`${base}.example`)} />
              </>
            )}

            {t(`${base}.flowpathTitle`, { defaultValue: '' }) && (
              <div className="mt-3">
                <h4 style={{ fontSize: '1rem', fontWeight: 600 }}>{t(`${base}.flowpathTitle`)}</h4>
                <p className="small text-secondary" style={{ lineHeight: 1.6 }}>
                  Taint findings include a <code>flowPath</code> array — the actual source→step→sink data-flow
                  path produced by the analysis engine. The AI triage agent uses this to assess exploitability.
                </p>
                <CodeBlock code={t(`${base}.flowpathExample`)} />
              </div>
            )}

            {t(`${base}.stateTitle`, { defaultValue: '' }) && (
              <div className="mt-3">
                <h4 style={{ fontSize: '1rem', fontWeight: 600 }}>{t(`${base}.stateTitle`)}</h4>
                <div className="table-responsive">
                  <table className="table table-sm table-bordered" style={{ fontSize: '0.85rem' }}>
                    <thead><tr><th>State</th><th>Meaning</th></tr></thead>
                    <tbody>
                      {Array.isArray(stateValues) && stateValues.map((sv, i) => (
                        <tr key={i}>
                          <td className="font-monospace fw-bold">{sv.state}</td>
                          <td>{sv.meaning}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {t(`${base}.contextFields`, { defaultValue: '' }) && (
              <div className="mt-3">
                <h4 style={{ fontSize: '1rem', fontWeight: 600 }}>Context Fields</h4>
                <p style={{ lineHeight: 1.75 }}>{t(`${base}.contextFields`)}</p>
              </div>
            )}
          </>
        );
      }

      case 'aiAssistant': {
        const commands = t(`${base}.commands`, { returnObjects: true });
        const triageActions = t(`${base}.triageActions`, { returnObjects: true });
        const roadmap = t(`${base}.roadmap`, { returnObjects: true });
        return (
          <>
            <p style={{ lineHeight: 1.75 }}>{body}</p>

            <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1.5rem' }}>Commands</h4>
            <div className="table-responsive">
              <table className="table table-sm table-bordered" style={{ fontSize: '0.85rem' }}>
                <thead>
                  <tr><th>Command</th><th>Description</th><th>Example</th></tr>
                </thead>
                <tbody>
                  {Array.isArray(commands) && commands.map((cmd, i) => (
                    <tr key={i}>
                      <td className="font-monospace fw-bold">{cmd.command}</td>
                      <td>{cmd.description}</td>
                      <td className="font-monospace" style={{ fontSize: '0.8rem' }}>{cmd.example}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {t(`${base}.triageOutput`, { defaultValue: '' }) && (
              <div className="mt-3">
                <h4 style={{ fontSize: '1rem', fontWeight: 600 }}>Triage Output Format</h4>
                <CodeBlock code={t(`${base}.triageOutput`)} />
              </div>
            )}

            {Array.isArray(triageActions) && triageActions.length > 0 && (
              <div className="mt-3">
                <h4 style={{ fontSize: '1rem', fontWeight: 600 }}>AI Triage Actions</h4>
                <p style={{ lineHeight: 1.75, fontSize: '0.9rem' }}>{t(`${base}.pipeline`, { defaultValue: '' })}</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '0.75rem', marginTop: '0.75rem' }}>
                  {triageActions.map((action, i) => (
                    <div key={i} style={{ background: '#1a1a2e', borderRadius: 8, padding: '0.75rem 1rem', border: '1px solid #2d2d44' }}>
                      <div style={{ fontSize: '1.2rem', marginBottom: '0.25rem' }}>
                        {action.icon} <strong style={{ fontSize: '0.85rem' }}>{action.action}</strong>
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#aaa', lineHeight: 1.5 }}>{action.description}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {t(`${base}.providers`, { defaultValue: '' }) && (
              <div className="mt-3">
                <h4 style={{ fontSize: '1rem', fontWeight: 600 }}>AI Providers</h4>
                <p style={{ lineHeight: 1.75 }}>{t(`${base}.providers`)}</p>
              </div>
            )}

            {Array.isArray(roadmap) && roadmap.length > 0 && (
              <div className="mt-3">
                <h4 style={{ fontSize: '1rem', fontWeight: 600 }}>AI Roadmap</h4>
                <div className="table-responsive">
                  <table className="table table-sm table-bordered" style={{ fontSize: '0.85rem' }}>
                    <thead>
                      <tr><th style={{ width: 40 }}></th><th>Phase</th><th>Features</th></tr>
                    </thead>
                    <tbody>
                      {roadmap.map((phase, i) => (
                        <tr key={i}>
                          <td style={{ textAlign: 'center' }}>{phase.status}</td>
                          <td className="fw-bold" style={{ whiteSpace: 'nowrap' }}>{phase.phase}</td>
                          <td>{phase.features}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        );
      }

      case 'builtinRules': {
        const rules = t(`${base}.rules`, { returnObjects: true });
        return (
          <>
            <p style={{ lineHeight: 1.75 }}>{body}</p>
            <div className="table-responsive">
              <table className="table table-sm table-bordered" style={{ fontSize: '0.85rem' }}>
                <thead>
                  <tr><th>Rule</th><th>Severity</th><th>CWE</th><th>Description</th></tr>
                </thead>
                <tbody>
                  {Array.isArray(rules) && rules.map((rule, i) => (
                    <tr key={i}>
                      <td className="font-monospace" style={{ whiteSpace: 'nowrap' }}>{rule.id}</td>
                      <td><SeverityBadge severity={rule.severity} /></td>
                      <td className="font-monospace">{rule.cwe}</td>
                      <td>{rule.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        );
      }

      case 'cicd': {
        const inputs = t(`${base}.inputs`, { returnObjects: true });
        const exits  = t(`${base}.exits`,  { returnObjects: true });
        return (
          <>
            <p style={{ lineHeight: 1.75 }}>{body}</p>
            <div className="fw-semibold mb-1">{t(`${base}.reusableTitle`)}</div>
            <CodeBlock code={t(`${base}.reusableCode`)} />
            <div className="fw-semibold mt-3 mb-1">{t('vigilDocs.inputs')}</div>
            <div className="table-responsive">
              <table className="table table-sm table-bordered" style={{ fontSize: '0.82rem' }}>
                <thead><tr><th>Input</th><th>Default</th><th>Description</th></tr></thead>
                <tbody>
                  {Array.isArray(inputs) && inputs.map((inp, i) => (
                    <tr key={i}>
                      <td className="font-monospace">{inp.input}</td>
                      <td className="font-monospace text-secondary">{inp.default}</td>
                      <td>{inp.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="fw-semibold mt-3 mb-1">{t(`${base}.exitTitle`)}</div>
            <div className="table-responsive">
              <table className="table table-sm table-bordered" style={{ fontSize: '0.82rem' }}>
                <thead><tr><th>Code</th><th>Meaning</th></tr></thead>
                <tbody>
                  {Array.isArray(exits) && exits.map((ex, i) => (
                    <tr key={i}>
                      <td className="font-monospace fw-bold">{ex.code}</td>
                      <td>{ex.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="fw-semibold mt-3 mb-1">{t(`${base}.gateTitle`)}</div>
            <CodeBlock code={t(`${base}.gateCode`)} />
          </>
        );
      }

      case 'sarif': {
        const fields = t(`${base}.fields`, { returnObjects: true });
        return (
          <>
            <p style={{ lineHeight: 1.75 }}>{body}</p>
            {t(`${base}.compatNote`, { defaultValue: '' }) && (
              <div className="alert alert-success mb-3" style={{ fontSize: '0.85rem', borderRadius: 8 }}>
                ✅ {t(`${base}.compatNote`)}
              </div>
            )}
            <div className="fw-semibold mb-1">{t(`${base}.uploadTitle`)}</div>
            <CodeBlock code={t(`${base}.uploadCode`)} />
            <div className="fw-semibold mt-3 mb-2">SARIF fields</div>
            <div className="table-responsive">
              <table className="table table-sm table-bordered" style={{ fontSize: '0.82rem' }}>
                <thead><tr><th>Field</th><th>Description</th></tr></thead>
                <tbody>
                  {Array.isArray(fields) && fields.map((f, i) => (
                    <tr key={i}>
                      <td><code style={{ fontSize: '0.78rem' }}>{f.field}</code></td>
                      <td>{f.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="fw-semibold mt-3 mb-1">{t(`${base}.importTitle`)}</div>
            <CodeBlock code={t(`${base}.importCode`)} />
          </>
        );
      }

      case 'sastIntegration': {
        const features = t(`${base}.features`, { returnObjects: true });
        return (
          <>
            <p style={{ lineHeight: 1.75 }}>{body}</p>
            {t(`${base}.navigateTo`, { defaultValue: '' }) && (
              <div className="alert alert-info mb-3" style={{ fontSize: '0.85rem', borderRadius: 8 }}>
                📍 Navigate to: <strong>{t(`${base}.navigateTo`)}</strong>
              </div>
            )}
            <BulletList items={Array.isArray(features) ? features : []} />
          </>
        );
      }

      case 'ruleStudio': {
        const tabs = t(`${base}.tabs`, { returnObjects: true });
        return (
          <>
            <p style={{ lineHeight: 1.75 }}>{body}</p>
            {t(`${base}.navigateTo`, { defaultValue: '' }) && (
              <div className="alert alert-info mb-3" style={{ fontSize: '0.85rem', borderRadius: 8 }}>
                📍 Navigate to: <strong>{t(`${base}.navigateTo`)}</strong>
              </div>
            )}
            {Array.isArray(tabs) && (
              <>
                <h4 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.75rem' }}>Tabs</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '0.75rem' }}>
                  {tabs.map((tab, i) => (
                    <div key={i} className="card shadow-sm">
                      <div className="card-body py-2 px-3">
                        <div className="fw-semibold mb-1">{tab.icon} {tab.name}</div>
                        <div className="small text-secondary">{tab.description}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
            {t(`${base}.promoteNote`, { defaultValue: '' }) && (
              <div className="alert alert-success mt-3" style={{ fontSize: '0.85rem', borderRadius: 8 }}>
                🚀 {t(`${base}.promoteNote`)}
              </div>
            )}
          </>
        );
      }

      case 'academy': {
        const modules = t(`${base}.modules`, { returnObjects: true });
        return (
          <>
            <p style={{ lineHeight: 1.75 }}>{body}</p>
            {t(`${base}.navigateTo`, { defaultValue: '' }) && (
              <div className="alert alert-info mb-3" style={{ fontSize: '0.85rem', borderRadius: 8 }}>
                📍 Navigate to: <strong>{t(`${base}.navigateTo`)}</strong>
              </div>
            )}
            {Array.isArray(modules) && (
              <div className="table-responsive">
                <table className="table table-sm table-bordered" style={{ fontSize: '0.85rem' }}>
                  <thead><tr><th>#</th><th>Module</th><th>Topics</th></tr></thead>
                  <tbody>
                    {modules.map((m, i) => (
                      <tr key={i}>
                        <td className="font-monospace text-secondary">{m.id}</td>
                        <td className="fw-semibold">{m.name}</td>
                        <td className="text-secondary">{m.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        );
      }

      case 'ruleDocumentation': {
        const steps    = t(`${base}.navigateSteps`, { returnObjects: true });
        const features = t(`${base}.pageFeatures`,  { returnObjects: true });
        return (
          <>
            <p style={{ lineHeight: 1.75 }}>{body}</p>
            <div className="fw-semibold mb-1">{t(`${base}.navigateTitle`)}</div>
            <ol style={{ lineHeight: 1.8, paddingLeft: '1.25rem', marginBottom: '1rem' }}>
              {Array.isArray(steps) && steps.map((s, i) => <li key={i}>{s}</li>)}
            </ol>
            <div className="fw-semibold mb-1">{t(`${base}.pageTitle`)}</div>
            <ul style={{ lineHeight: 1.8, paddingLeft: '1.25rem' }}>
              {Array.isArray(features) && features.map((f, i) => <li key={i}>{f}</li>)}
            </ul>
          </>
        );
      }

      case 'api': {
        const endpoints = t(`${base}.endpoints`, { returnObjects: true });
        return (
          <>
            <p style={{ lineHeight: 1.75 }}>{body}</p>
            <div className="table-responsive">
              <table className="table table-sm table-bordered" style={{ fontSize: '0.85rem' }}>
                <thead>
                  <tr><th>Method</th><th>Path</th><th>Description</th></tr>
                </thead>
                <tbody>
                  {Array.isArray(endpoints) && endpoints.map((ep, i) => (
                    <tr key={i}>
                      <td><span className="badge bg-primary">{ep.method}</span></td>
                      <td className="font-monospace">{ep.path}</td>
                      <td>{ep.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        );
      }

      case 'baseline': {
        const steps = t(`${base}.steps`, { returnObjects: true });
        return (
          <>
            <p style={{ lineHeight: 1.75 }}>{body}</p>
            {Array.isArray(steps) && steps.map((step, i) => (
              <div key={i} className="mb-3">
                <div className="fw-semibold mb-1">{step.title}</div>
                <CodeBlock code={step.code} />
              </div>
            ))}
          </>
        );
      }

      case 'suppression': {
        return (
          <>
            <p style={{ lineHeight: 1.75 }}>{body}</p>
            <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1rem' }}>Waiver File Format</h4>
            <CodeBlock code={t(`${base}.waiver_format`, { defaultValue: '' })} />
            <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1.25rem' }}>Usage</h4>
            <CodeBlock code={t(`${base}.usage`, { defaultValue: '' })} />
            {t(`${base}.note`, { defaultValue: '' }) && (
              <div className="alert alert-info mt-3" style={{ fontSize: '0.85rem', borderRadius: 8 }}>
                💡 {t(`${base}.note`)}
              </div>
            )}
          </>
        );
      }

      case 'testingQuality': {
        const cmds = t(`${base}.commands`, { returnObjects: true });
        return (
          <>
            <p style={{ lineHeight: 1.75 }}>{body}</p>
            <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1rem' }}>Embedding Tests in Rule Files</h4>
            <CodeBlock code={t(`${base}.testBlock`, { defaultValue: '' })} />
            {Array.isArray(cmds) && (
              <>
                <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1.25rem' }}>Commands</h4>
                {cmds.map((cmd, i) => (
                  <div key={i} className="mb-3">
                    <div className="fw-semibold font-monospace mb-1">{cmd.command} — <span className="fw-normal text-secondary">{cmd.description}</span></div>
                    <CodeBlock code={cmd.usage} />
                  </div>
                ))}
              </>
            )}
            {t(`${base}.coverageOutput`, { defaultValue: '' }) && (
              <>
                <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1.25rem' }}>Example Output</h4>
                <CodeBlock code={t(`${base}.coverageOutput`)} />
              </>
            )}
          </>
        );
      }

      case 'benchmark': {
        return (
          <>
            <p style={{ lineHeight: 1.75 }}>{body}</p>
            <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1rem' }}>Command</h4>
            <CodeBlock code={t(`${base}.command`, { defaultValue: '' })} />
            <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1.25rem' }}>Manifest File</h4>
            <CodeBlock code={t(`${base}.manifestExample`, { defaultValue: '' })} />
            <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1.25rem' }}>Sample Output</h4>
            <CodeBlock code={t(`${base}.sampleOutput`, { defaultValue: '' })} />
          </>
        );
      }

      case 'registry': {
        const packs = t(`${base}.builtinPacks`, { returnObjects: true });
        return (
          <>
            <p style={{ lineHeight: 1.75 }}>{body}</p>
            {Array.isArray(packs) && (
              <>
                <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1rem' }}>Built-in Packs</h4>
                <div className="table-responsive">
                  <table className="table table-sm table-bordered" style={{ fontSize: '0.85rem' }}>
                    <thead><tr><th>Pack</th><th>Description</th></tr></thead>
                    <tbody>
                      {packs.map((p, i) => (
                        <tr key={i}>
                          <td className="font-monospace fw-bold">{p.name}</td>
                          <td>{p.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1.25rem' }}>Commands</h4>
            <CodeBlock code={t(`${base}.commands`, { defaultValue: '' })} />
            <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1.25rem' }}>Pack Structure</h4>
            <CodeBlock code={t(`${base}.packStructure`, { defaultValue: '' })} />
          </>
        );
      }

      default:
        return <p style={{ lineHeight: 1.75 }}>{body}</p>;
    }
  };

  return (
    <div className="App-body">
      <div className="ce-page-header" style={{ paddingLeft: 0, paddingRight: 0, marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div>
            <h1 className="ce-page-title">🛡️ {t('vigilDocs.title')}</h1>
            <p className="ce-page-subtitle">{t('vigilDocs.subtitle')}</p>
            <span className="badge bg-success mt-1">{t('vigilDocs.license')}</span>
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <Link className="btn btn-outline-primary btn-sm" to="/docs/vigil/tutorial">
              {t('vigilDocs.openTutorial')} →
            </Link>
            <Link className="btn btn-outline-secondary btn-sm" to="/rule-editor">
              {t('vigilDocs.openRuleEditor')}
            </Link>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: '2.5rem', alignItems: 'start' }}>
        <div className="docs-layout-toc" style={{ position: 'sticky', top: '1.5rem' }}>
          <div className="section-title" style={{ marginBottom: '0.625rem' }}>{t('vigilDocs.contents')}</div>
          {SECTIONS.map((section) => (
            <a
              key={section.id}
              href={`#${section.id}`}
              style={{
                display: 'block', padding: '0.3rem 0.625rem', borderRadius: 'var(--radius-xs)',
                fontSize: '0.8125rem', color: 'var(--bs-secondary-color)', textDecoration: 'none', marginBottom: '1px',
              }}
            >
              {section.icon} {t(`vigilDocs.sections.${section.id}.title`)}
            </a>
          ))}
        </div>

        <div className="docs-layout-content">
          {SECTIONS.map((section) => (
            <section key={section.id} id={section.id} style={{ marginBottom: '2.5rem', scrollMarginTop: '1.5rem' }}>
              <SectionHeading icon={section.icon} title={t(`vigilDocs.sections.${section.id}.title`)} />
              {renderSection(section)}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
